import React, { useState } from "react";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { TextField } from "../ui/components/TextField";
import { TextArea } from "../ui/components/TextArea";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import { FeatherX, FeatherPlus } from "@subframe/core";
import { createCustomerGroup } from "../services/api";

interface CreateCustomerGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGroupCreated: (group: {
    group_id: number;
    group_name: string;
    group_description?: string;
  }) => void;
}

const CreateCustomerGroupModal: React.FC<CreateCustomerGroupModalProps> = ({
  open,
  onOpenChange,
  onGroupCreated,
}) => {
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = () => {
    setGroupName("");
    setGroupDescription("");
    setError(null);
    onOpenChange(false);
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setError("Group name is required");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const response = await createCustomerGroup({
        group_name: groupName,
        group_description: groupDescription || undefined,
      });

      // Call the callback with the created group
      onGroupCreated(response.data);

      // Reset form and close modal
      setGroupName("");
      setGroupDescription("");
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating customer group:", error);
      setError(
        error instanceof Error ? error.message : "Failed to create group"
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-144 flex-col items-start gap-6 bg-white px-8 py-8">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            New Customer Group
          </span>
          <IconButton
            icon={<FeatherX />}
            onClick={handleCancel}
            disabled={isCreating}
          />
        </div>
        <div className="flex w-full flex-col items-start gap-4">
          {error && (
            <div className="w-full rounded-md bg-error-50 border border-error-200 px-4 py-3">
              <span className="text-body font-body text-error-700">
                {error}
              </span>
            </div>
          )}
          <TextField
            className="h-auto w-full flex-none"
            variant="filled"
            label="Group Name"
            helpText="Enter a descriptive name for the customer group"
            error={!!error && !groupName.trim()}
          >
            <TextField.Input
              placeholder="e.g. Enterprise Clients"
              value={groupName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setGroupName(event.target.value);
                setError(null);
              }}
            />
          </TextField>
          <TextArea
            className="h-auto w-full flex-none"
            variant="filled"
            label="Description"
            helpText="Provide details about this customer group"
          >
            <TextArea.Input
              placeholder="Describe the purpose and scope of this customer group..."
              value={groupDescription}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                setGroupDescription(event.target.value);
              }}
            />
          </TextArea>
        </div>
        <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
          <Button
            variant="neutral-tertiary"
            onClick={handleCancel}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            icon={<FeatherPlus />}
            onClick={handleCreateGroup}
            disabled={isCreating}
            loading={isCreating}
          >
            {isCreating ? "Creating..." : "Create Group"}
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default CreateCustomerGroupModal;
